// Standard AP column types for APDataTable (spec § Forms & data):
// dayjs dates with '—' fallback, status Badge pills, row-actions dropdown.
import { MenuDotsLinear } from '@assessprep/icons/solar';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link';

/** `.neo/rules/frontend.md § Dates / times` — '—' for null/invalid, comma between date and time. */
export const formatDateTime = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD, HH:mm') : '—';
};

export const formatDate = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '—';
};

interface APColumnBase<TData> {
  /** Deep accessor path into the row, e.g. 'attributes.updated_at' (JSON:API rows). */
  accessorKey: string;
  /** Already-translated header label. */
  header: string;
  id?: string;
  enableSorting?: boolean;
  size?: number;
  meta?: ColumnDef<TData>['meta'];
}

/** Date/time column — renders via the dayjs helper, never a raw ISO string. */
export function apDateColumn<TData>({
  accessorKey,
  header,
  id,
  enableSorting = false,
  size,
  meta,
  withTime = true,
}: APColumnBase<TData> & { withTime?: boolean }): ColumnDef<TData> {
  return {
    id: id ?? accessorKey,
    accessorKey,
    header,
    enableSorting,
    size,
    meta: { skeleton: <Skeleton className="h-4 w-28" />, ...meta },
    cell: ({ getValue }) => (
      <span className="tabular-nums">
        {withTime
          ? formatDateTime(getValue() as string | null | undefined)
          : formatDate(getValue() as string | null | undefined)}
      </span>
    ),
  };
}

/** Status pill column — value → Badge variant + already-translated label. */
export function apStatusColumn<TData>({
  accessorKey,
  header,
  id,
  enableSorting = false,
  size,
  meta,
  variants = {},
  labels = {},
  defaultVariant = 'secondary',
  renderExtra,
}: APColumnBase<TData> & {
  /** Map of raw status value → Badge variant. */
  variants?: Record<string, BadgeVariant>;
  /** Map of raw status value → translated label; falls back to the raw value. */
  labels?: Record<string, string>;
  defaultVariant?: BadgeVariant;
  /** Optional adornment after the pill (e.g. a release-date info tooltip). */
  renderExtra?: (value: string, row: TData) => ReactNode;
}): ColumnDef<TData> {
  return {
    id: id ?? accessorKey,
    accessorKey,
    header,
    enableSorting,
    size,
    meta: { skeleton: <Skeleton className="h-5 w-16 rounded-full" />, ...meta },
    cell: ({ getValue, row }) => {
      const value = getValue() as string | null | undefined;
      if (!value) return <span>—</span>;
      return (
        <span className="inline-flex items-center gap-1.5">
          <Badge variant={variants[value] ?? defaultVariant} data-testid={`status-${value}`}>
            {labels[value] ?? value}
          </Badge>
          {renderExtra?.(value, row.original)}
        </span>
      );
    },
  };
}

export interface APRowAction<TData> {
  key: string;
  /** Already-translated label. */
  label: ReactNode;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: (row: TData) => boolean;
  hidden?: (row: TData) => boolean;
  onSelect: (row: TData) => void;
}

/** Row-actions column — kebab dropdown at the row end. */
export function apRowActionsColumn<TData>({
  actions,
  header = '',
  ariaLabel,
  size = 56,
}: {
  actions: APRowAction<TData>[];
  header?: string;
  /** Already-translated aria-label for the kebab trigger. */
  ariaLabel: string;
  size?: number;
}): ColumnDef<TData> {
  return {
    id: 'ap-row-actions',
    header,
    enableSorting: false,
    enableHiding: false,
    size,
    meta: {
      skeleton: <Skeleton className="size-7" />,
      cellClassName: 'text-end',
      headerClassName: 'text-end',
    },
    cell: ({ row }) => {
      const visibleActions = actions.filter((action) => !action.hidden?.(row.original));
      if (visibleActions.length === 0) return null;

      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={ariaLabel}
              data-testid="row-actions-trigger"
              onClick={(event) => event.stopPropagation()}
            >
              <MenuDotsLinear aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {visibleActions.map((action) => (
              <DropdownMenuItem
                key={action.key}
                variant={action.destructive ? 'destructive' : 'default'}
                disabled={action.disabled?.(row.original)}
                data-testid={`row-action-${action.key}`}
                onClick={(event) => {
                  event.stopPropagation();
                  action.onSelect(row.original);
                }}
              >
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  };
}

/** Plain text column with a sensible skeleton. */
export function apTextColumn<TData>({
  accessorKey,
  header,
  id,
  enableSorting = false,
  size,
  meta,
  render,
}: APColumnBase<TData> & { render?: (value: unknown, row: TData) => ReactNode }): ColumnDef<TData> {
  return {
    id: id ?? accessorKey,
    accessorKey,
    header,
    enableSorting,
    size,
    meta: { skeleton: <Skeleton className="h-4 w-24" />, ...meta },
    ...(render ? { cell: ({ getValue, row }) => render(getValue(), row.original) } : {}),
  };
}
